import { FC, useCallback } from "react";
import { useParams, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";

import { AppleMap } from "capitalrx-components-2.0";
import { Icon, LoadingGroup, LoadingSkeleton } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Paragraph, Subheading, Subtitle } from "capitalrx-components-2.0/lib/typography";

import { useGetDrugQuery } from "@/App/api/drug";
import { useGetPharmacyQuery } from "@/App/api/pharmacy";
import { ContentPanel, DrugDetails, PageWrapper, PaymentDetails, PharmacyDetails, ThemedButton } from "@/App/components";
import { useNavigateClient } from "@/App/hooks";
import { ANALYTIC_EVENTS, logEvent } from "@/App/service/analyticsService";
import { getPharmacyDescription } from "@/App/utils/utils";
import styled from "styled-components";

const LargeIcon = styled(Icon)`
  min-height: unset !important;
  max-height: unset !important;
  min-width: unset !important;
  max-width: unset !important;
  width: 32px;
  height: 32px;
`;

export const FindBestPricePricingInfoPage: FC = () => {
  const { t: translate } = useTranslation();
  const navigate = useNavigateClient();
  const [searchParams] = useSearchParams();
  const { drug_id: drugId } = useParams();

  const { isCompressed } = useResponsive({
    isCompressed: {
      mobile: true,
      desktop: false,
    },
  });

  const dosageForm = searchParams.get("dosage_form");
  const dosage = searchParams.get("dosage");
  const quantity = searchParams.get("quantity");
  const daysSupply = searchParams.get("days_supply");
  const location: { readonly latitude: string; readonly longitude: string } = JSON.parse(
    decodeURIComponent(searchParams.get("loc") || "{}"),
  );
  const providerId = searchParams.get("provider_id");

  const patientPayAmount = decodeURIComponent(searchParams.get("patient_pay") || "");
  const planPayAmount = decodeURIComponent(searchParams.get("plan_pay") || "");
  const rejectMessage = decodeURIComponent(searchParams.get("reject_message") || "");

  const { data: drug, isLoading: isLoadingDrug } = useGetDrugQuery(+drugId);
  const { data: pharmacy, isLoading: isLoadingPharmacy } = useGetPharmacyQuery(providerId);

  const onBackPressed = useCallback(() => {
    const infoParams = new URLSearchParams();
    infoParams.set("dosage_form", dosageForm);
    infoParams.set("dosage", dosage);
    infoParams.set("quantity", quantity);
    infoParams.set("days_supply", daysSupply);
    infoParams.set("loc", searchParams.get("loc"));
    const submissionClarificationCode = searchParams.get("submission_clarification_code");
    const hasShownCapRxMessage = searchParams.get("rxCapModalShown");
    if (submissionClarificationCode != null) {
      infoParams.set("submission_clarification_code", submissionClarificationCode);
    }
    if (hasShownCapRxMessage != null) {
      infoParams.set("rxCapModalShown", hasShownCapRxMessage);
    }

    logEvent(ANALYTIC_EVENTS.PRESCRIPTION.backButton);

    navigate({
      pathname: `/find-best-price/compare-prices/${drugId}`,
      search: infoParams.toString(),
    });
  }, [dosageForm, dosage, quantity, daysSupply, location]);

  return (
    <PageWrapper>
      <ContentPanel
        title={
          <Stack>
            <Shelf>
              <ThemedButton testId="go-back" onClick={onBackPressed} appearance="secondary" icon="arrow-left">
                {translate("FIND_BEST_PRICE.findBestPricePricingInfoPage.back")}
              </ThemedButton>
              <LoadingGroup loading={isLoadingDrug}>
                <LoadingGroup.Placeholder fallback={<LoadingSkeleton style={{ width: 120, height: 36 }} />}>
                  <Subtitle testId="drug-name">{drug?.drug?.drug_name}</Subtitle>
                </LoadingGroup.Placeholder>
              </LoadingGroup>
              <Shelf.Spacer />
            </Shelf>
            <Shelf>
              <Stack spacing="none">
                <LoadingGroup loading={isLoadingDrug}>
                  <LoadingGroup.Placeholder fallback={<LoadingSkeleton style={{ width: 120, height: 24 }} />}>
                    <Body testId="drug-info">
                      {dosageForm} | {dosage} | {quantity} | {daysSupply} {translate("FIND_BEST_PRICE.findBestPricePricingInfoPage.daysSupply")}
                    </Body>
                  </LoadingGroup.Placeholder>
                </LoadingGroup>

                <LoadingGroup loading={isLoadingPharmacy}>
                  <LoadingGroup.Placeholder fallback={<LoadingSkeleton style={{ width: 120, height: 36 }} />}>
                    {!!pharmacy && (
                      <Body testId="pharmacy-info">
                        {pharmacy.name} | {getPharmacyDescription(pharmacy, location)}
                      </Body>
                    )}
                  </LoadingGroup.Placeholder>
                </LoadingGroup>
              </Stack>
              <Shelf.Spacer />
            </Shelf>
          </Stack>
        }
      >
        <Grid>
          {rejectMessage.length > 0 && (
            <Grid.Span columns={12}>
              <Shelf alignment="center" testId="rejection-message-container">
                <Stack>
                  <Shelf>
                    <Shelf.Spacer />
                    <LargeIcon name="x-circle" color="red" />
                    <Shelf.Spacer />
                  </Shelf>
                  <Subheading textAlign="center">{translate("FIND_BEST_PRICE.findBestPricePricingInfoPage.pharmacyHasRejected")}</Subheading>
                  <Paragraph textAlign="center" testId="rejection-messages">{translate("FIND_BEST_PRICE.findBestPricePricingInfoPage.reasonForRejection")}{rejectMessage}</Paragraph>
                </Stack>
              </Shelf>
            </Grid.Span>
          )}
          {rejectMessage.length === 0 && (
            <Grid.Span columns={12}>
              <PaymentDetails usageContext="fbp" planPayAmount={+planPayAmount} patientPayAmount={+patientPayAmount} />
            </Grid.Span>
          )}
          {rejectMessage.length === 0 && (
            <Grid.Span columns={isCompressed ? 12 : 6}>
              <PharmacyDetails {...{ ...pharmacy, isLoading: isLoadingPharmacy }} />
            </Grid.Span>
          )}
          {rejectMessage.length === 0 && (
            <Grid.Span columns={isCompressed ? 12 : 6}>
              <DrugDetails
                quantity={quantity}
                drugName={drug?.drug?.drug_name}
                daysSupply={daysSupply}
                dosage={dosage}
                dosageForm={dosageForm}
                drugType={drug?.drug?.drug_type}
              />
            </Grid.Span>
          )}
          <Grid.Span columns={12}>
            {pharmacy && <AppleMap
              center={{ latitude: parseFloat(pharmacy.latitude), longitude: parseFloat(pharmacy.longitude) }}
              locations={[{
                latitude: parseFloat(pharmacy.latitude),
                longitude: parseFloat(pharmacy.longitude),
                title: pharmacy.name,
                data: {
                  pharmacy,
                },
              }]}
            />}
          </Grid.Span>
        </Grid>
      </ContentPanel>
    </PageWrapper>
  );
};
